import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, effect, signal } from '@angular/core';
import { Store } from '@ngneat/elf';
import { PlotlyService } from 'angular-plotly.js';
import { Subscription } from 'rxjs';
import { TrajectoryXyzModel } from 'src/app/shared/models/trajectory-xyz.model';
import { buildTrajectory3dPlot, rotateCameraEye } from '../shared/helpers/trajectory-3d-plot.helper';

@Component({
    selector: 'app-three-dee-plot',
    templateUrl: './3Dplot.component.html',
  styles: [':host { display: flex; flex: 1 1 0%; min-height: 0; overflow: hidden; }'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThreeDeePlotComponent implements OnInit, OnDestroy {
  private _intervalID = 0;
  private readonly _rotationStep = Math.PI / 180;
  private _plotPoints: TrajectoryXyzModel | null = null;
  private _subscriptions = new Subscription();
  private _storeSubscription: Subscription | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _resizeFrame: number | null = null;
  private _observedWidth = 0;
  private _observedHeight = 0;
  private _isResizeInFlight = false;

  private _xyzData = signal<TrajectoryXyzModel | null>(null);
  private _longLengths = signal('');
  private _isDarkMode = signal(true);
  private _isRotating = signal(false);
  private _isShowingProjections = signal(false);

  @Input()
  public set xyzData(data: TrajectoryXyzModel | null) {
    if (data) {
      this._plotPoints = data;
      this._xyzData.set(data);
    }
  }

  @Input() public set trajectoryPlotStore(store: Store | null) {
    if (store) {
      this._storeSubscription?.unsubscribe();

      // Subscribe to store state changes for Angular 21 change detection.
      // Also manages the rotation interval so IDB hydration (which fires after
      // ngAfterViewInit) correctly starts rotation when state is restored.
      this._storeSubscription = store.subscribe(state => {
        this._isRotating.set(state.isRotating ?? false);
        this._isShowingProjections.set(state.isShowingProjections ?? false);
      });
      this._subscriptions.add(this._storeSubscription);
    }
  }

  @Input() public set longLengths(value: string | null) {
    this._longLengths.set(value ?? '');
  }

  @Input() public set isDarkMode(value: boolean | null) {
    this._isDarkMode.set(value ?? true);
  }

  constructor(private _plotlyService: PlotlyService, private _elementRef: ElementRef<HTMLElement>) {
    effect(() => {
      const plotData = this._xyzData();
      this._isShowingProjections();
      this._isDarkMode();
      this._longLengths();

      if (plotData) {
        this.setPlotData(plotData);
      }
    });

    // Effect to manage turntable rotation interval
    effect((onCleanup) => {
      if (!this._isRotating()) {
        clearInterval(this._intervalID);
        return;
      }

      this._intervalID = setInterval(() => this.createTurntableRotationNextFrame(), 42) as any;
      onCleanup(() => clearInterval(this._intervalID));
    });
  }

  // Use signals for reactive state that updates from intervals
  public plotlyData = signal<unknown[]>([]);
  public layout = signal<Record<string, unknown> | null>(null);
  public readonly plotId = `trajectory-3d-${Math.random().toString(36).slice(2)}`;

  public config = {
    responsive: true,
    displaylogo: false,
    scrollZoom: true,
    toImageButtonOptions: {
      filename: 'trajectory_plot',
      width: 500,
      height: 500,
      format: 'png'
    }
  };

  public onRotationCheckChange(e: { checked?: boolean }) {
    this._isRotating.set(!!e?.checked);
  }

  public onProjectionsCheckChange(e: { checked?: boolean }) {
    this._isShowingProjections.set(!!e?.checked);
    if (this._plotPoints) {
      this.setPlotData(this._plotPoints);
    }
  }

  ngOnInit(): void {
    this._initResizeObserver();
  }

  private setPlotData(plotData: TrajectoryXyzModel) {
    if (!plotData) {
      return;
    }
    const build = buildTrajectory3dPlot({
      plotData,
      isShowingProjections: this._isShowingProjections(),
      isDarkMode: this._isDarkMode(),
      longLengths: this._longLengths()
    });

    this.layout.set(build.layout);
    this.plotlyData.set(build.plotlyData);
  }

  private createTurntableRotationNextFrame() {
    const currentLayout = this.layout();
    if (!currentLayout) {
      return;
    }

    const updatedLayout = structuredClone(currentLayout) as Record<string, unknown>;
    const scene = updatedLayout['scene'] as { camera?: { eye?: { x: number; y: number; z: number } } } | undefined;
    const camera = scene?.camera;
    const currentEye = camera?.eye;
    if (!camera || !currentEye) {
      return;
    }

    camera.eye = rotateCameraEye(currentEye, this._rotationStep);
    updatedLayout['showlegend'] = false;
    // Signals automatically trigger change detection with OnPush
    this.layout.set(updatedLayout);
  }

  private _initResizeObserver(): void {
    const hostElement = this._elementRef.nativeElement;
    const observedElement = hostElement.parentElement ?? hostElement;

    this._resizeObserver = new ResizeObserver(entries => {
      const nextHeight = entries.reduce((maxHeight, entry) => Math.max(maxHeight, entry?.contentRect?.height ?? 0), 0);
      const nextWidth = entries.reduce((maxWidth, entry) => Math.max(maxWidth, entry?.contentRect?.width ?? 0), 0);
      const heightChanged = Math.abs(nextHeight - this._observedHeight) >= 0.5;
      const widthChanged = Math.abs(nextWidth - this._observedWidth) >= 0.5;

      this._observedHeight = nextHeight;
      this._observedWidth = nextWidth;

      if (nextHeight < 120 || nextWidth < 120 || (!heightChanged && !widthChanged)) {
        return;
      }

      this._schedulePlotResize();
    });

    this._resizeObserver.observe(observedElement);
    if (observedElement !== hostElement) {
      this._resizeObserver.observe(hostElement);
    }
  }

  private _schedulePlotResize(): void {
    if (this._resizeFrame !== null) {
      window.cancelAnimationFrame(this._resizeFrame);
    }

    this._resizeFrame = window.requestAnimationFrame(() => {
      this._resizeFrame = null;
      void this._applyPlotResize();
    });
  }

  private async _applyPlotResize(): Promise<void> {
    if (!this.layout() || !this.plotlyData().length) {
      return;
    }
    
    if (this._isResizeInFlight) {
      setTimeout(() => this._schedulePlotResize(), 100);
      return;
    }

    const graphDiv = this._plotlyService.getInstanceByDivId(this.plotId) as any;
    if (!graphDiv || !graphDiv._fullLayout || !graphDiv._context || graphDiv.isConnected === false) {
      return;
    }

    this._isResizeInFlight = true;
    try {
      const Plotly = await this._plotlyService.getPlotly();
      await Plotly.Plots.resize(graphDiv);
    } finally {
      this._isResizeInFlight = false;
    }
  }

  ngOnDestroy(): void {
    clearInterval(this._intervalID);
    this._resizeObserver?.disconnect();
    if (this._resizeFrame !== null) {
      window.cancelAnimationFrame(this._resizeFrame);
      this._resizeFrame = null;
    }
    this._storeSubscription?.unsubscribe();
    this._subscriptions.unsubscribe();
  }

}
